// SPDX-FileCopyrightText: 2024 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { Colors } from '@tls/treact-ui';
import React, { useState } from 'react';
import styled from 'styled-components';
import UsedLabels from './UsedLabels';
import PreviewImage from './PreviewImage';
import ProcessTag from 'components/common/ProcessTag';
import useNavigationFunctions from 'hooks/useNavigationFunctions';
import EditTagsDialog from './EditTagsDialog';
import TagContainer from './TagContainer';
import DataElementOptions from './DataElementOptions';
import LabeledIndicator from './helper/LabeledIndicator';
import { ModelClassMessage } from '@tls/sw91-communication/types/com.base';
import { LabelPackageInfoMessage } from '@tls/sw91-communication/types/com.api_db_access';

interface Props {
  labelPackage: LabelPackageInfoMessage;
  projectTags: string[];
  isQC: boolean;
  modelClasses: ModelClassMessage[];
}

export default function DataTile({ labelPackage, projectTags, isQC, modelClasses }: Readonly<Props>) {
  const { navigateToLabel, navigateToAlgorithms } = useNavigationFunctions();
  const [isTagDialogOpen, setIsTagDialogOpen] = useState(false);

  return (
    <Root data-testid={`data-image-${labelPackage.imageMd5 ?? 'unknown'}-tile`}>
      <Header>
        {isQC && (
          <>
            {labelPackage?.hasMask && labelPackage?.qcResult !== undefined ? (
              <ProcessTag processTag={labelPackage.qcResult} size={'24px'} />
            ) : (
              <Placeholder />
            )}
          </>
        )}
        <LabeledIndicator hasMask={labelPackage?.hasMask} isValidation={labelPackage?.isValidation} isQC={isQC} />
        <ImageName>{labelPackage.imageName}</ImageName>
        <DataElementOptions
          noBorder
          onOptionClick={id => {
            if (id === 'editLabel') {
              navigateToLabel(labelPackage.imageMd5);
            } else if (id === 'editImageTags') {
              setIsTagDialogOpen(true);
            } else if (id === 'editAlgorithms') {
              navigateToAlgorithms(labelPackage.imageMd5);
            }
          }}
        />
      </Header>
      <ImageContainer data-testid={`data-image-${labelPackage.imageMd5 ?? 'unknown'}-preview`}>
        <PreviewImage previewData={labelPackage?.previewData} />
      </ImageContainer>
      <Divider />
      <UsedLabels usedLabels={labelPackage.classIndicesInMask} isLoading={false} modelClasses={modelClasses} />
      <Spacer />
      <Divider />
      <TagContainer tags={labelPackage.tags} />
      <EditTagsDialog
        projectTags={projectTags}
        labelPackage={labelPackage}
        isOpen={isTagDialogOpen}
        onClose={() => setIsTagDialogOpen(false)}
      />
    </Root>
  );
}

const Root = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: 17rem;
  min-width: 17rem;
  box-shadow:
    0 2px 4px 0 rgba(0, 0, 0, 0.05),
    0 2px 8px 0 rgba(161, 161, 161, 0.4);
  padding: 0 0.5rem;
  border: 1px solid ${Colors.trgrey5.hex};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  column-gap: 0.5rem;
  padding: 0.5rem 0;
  .moveRight {
    margin-right: -0.5rem;
  }
`;
const Spacer = styled.div`
  flex: 1;
`;

const Divider = styled.div`
  border-bottom: 1px solid ${Colors.trgrey5.hex};
  margin: 0.5rem 0;
`;
const Placeholder = styled.div`
  width: 24px;
`;

const ImageName = styled.div`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  flex: 1;
`;
const ImageContainer = styled.div`
  height: 140px;
`;
